/**
 * /app/$tenantSlug/attendance/regularize — Regularize (self).
 *
 * Lists the caller's recent day rows and lets them file a correction
 * (missed punch / wrong time / WFH / on-duty) for a date. A pending
 * regularization for a date blocks a second (409 pending_request_exists).
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useMyDays,
  useRegularizations,
  useCreateRegularization,
  useCancelRegularization,
} from "@/hooks/use-attendance";
import type { RegReasonType } from "@/types/attendance";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/regularize",
)({
  component: RegularizeRoute,
});

const REASONS: { value: RegReasonType; label: string }[] = [
  { value: "missed_punch", label: "Missed punch" },
  { value: "wrong_time", label: "Wrong time" },
  { value: "wfh", label: "Work from home" },
  { value: "on_duty", label: "On duty" },
  { value: "other", label: "Other" },
];

function RegularizeRoute() {
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  }, []);
  const days = useMyDays(range.from, range.to);
  const regs = useRegularizations(undefined, "self");
  const create = useCreateRegularization();
  const cancel = useCancelRegularization();

  const [workDate, setWorkDate] = useState("");
  const [reason, setReason] = useState<RegReasonType>("missed_punch");
  const [inAt, setInAt] = useState("");
  const [outAt, setOutAt] = useState("");
  const [note, setNote] = useState("");

  async function submit() {
    if (!workDate || !note.trim()) {
      toast.error("Date and reason note are required");
      return;
    }
    try {
      await create.mutateAsync({
        work_date: workDate,
        reason_type: reason,
        requested_in_at: inAt ? new Date(inAt).toISOString() : null,
        requested_out_at: outAt ? new Date(outAt).toISOString() : null,
        reason_note: note.trim(),
      });
      toast.success("Regularization submitted");
      setWorkDate("");
      setInAt("");
      setOutAt("");
      setNote("");
    } catch (e) {
      const err = toApiError(e);
      toast.error(
        err.error === "pending_request_exists"
          ? "A pending request already exists for that date"
          : (err.message ?? "Failed to submit"),
      );
    }
  }

  return (
    <TimeAndLeaveShell
      title="Regularize attendance"
      description="Fix a missed or wrong punch. Your manager or HR will review it."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>New correction</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="wd">Date</Label>
              <Input
                id="wd"
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as RegReasonType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="in">Corrected in (optional)</Label>
              <Input
                id="in"
                type="datetime-local"
                value={inAt}
                onChange={(e) => setInAt(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="out">Corrected out (optional)</Label>
              <Input
                id="out"
                type="datetime-local"
                value={outAt}
                onChange={(e) => setOutAt(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="note">Reason note</Label>
            <Textarea
              id="note"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain what happened"
            />
          </div>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? "Submitting…" : "Submit regularization"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>My recent days</CardTitle>
        </CardHeader>
        <CardContent>
          {days.data && days.data.days.length > 0 ? (
            <ul className="divide-y text-sm">
              {days.data.days.map((d) => (
                <li key={d.work_date} className="flex items-center justify-between py-2">
                  <span>{d.work_date}</span>
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{d.status}</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setWorkDate(d.work_date)}
                    >
                      Fix this day
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">No recent days.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>My regularizations</CardTitle>
        </CardHeader>
        <CardContent>
          {regs.data && regs.data.regularizations.length > 0 ? (
            <ul className="divide-y text-sm">
              {regs.data.regularizations.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2">
                  <span>
                    {r.work_date} — {r.reason_type}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{r.status}</Badge>
                    {r.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancel.mutate(r.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">No requests yet.</p>
          )}
        </CardContent>
      </Card>
      </div>
    </TimeAndLeaveShell>
  );
}
